import { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FileQuestion,
  BookOpen,
  FolderTree,
  Users,
  Settings,
  CreditCard,
  Receipt,
  Activity,
  LogOut,
  Menu,
  X,
  ChevronDown,
} from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';

interface AdminLayoutProps {
  children: ReactNode;
}

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/exams', label: 'Examens', icon: BookOpen },
  { path: '/admin/categories', label: 'Catégories', icon: FolderTree },
  { path: '/admin/questions', label: 'Questions', icon: FileQuestion },
  { path: '/admin/users', label: 'Utilisateurs', icon: Users },
  { path: '/admin/activity', label: 'Activité', icon: Activity },
  { path: '/admin/payments', label: 'Paiements', icon: CreditCard },
  { path: '/admin/payments/transactions', label: 'Transactions', icon: Receipt },
  { path: '/admin/settings', label: 'Paramètres', icon: Settings },
];

/**
 * Chemin actif : correspondance par préfixe, mais seule l'entrée la PLUS
 * spécifique gagne (« Paiements » ne se surligne pas sur /admin/payments/
 * transactions, couvert par « Transactions »).
 */
function activeNavPath(pathname: string): string | null {
  const matches = navItems.filter(
    (item) =>
      pathname === item.path ||
      (item.path !== '/admin' && pathname.startsWith(item.path + '/')),
  );
  if (matches.length === 0) return pathname.startsWith('/admin') ? '/admin' : null;
  return matches.reduce((a, b) => (b.path.length > a.path.length ? b : a)).path;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Mobile header */}
      <div className="lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <span className="font-bold text-lg text-emerald-600">Exam Copilot Admin</span>
        <div className="w-10" />
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="h-full flex flex-col">
            {/* Logo */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Link to="/admin" className="flex items-center gap-2">
                <img src="/logo.svg" alt="Exam Copilot" className="w-10 h-10 rounded-lg" />
                <div>
                  <h1 className="font-bold text-lg text-gray-900 dark:text-white">Exam Copilot</h1>
                  <p className="text-xs text-gray-500">Administration</p>
                </div>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
              {navItems.map((item) => {
                const isActive = activeNavPath(location.pathname) === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <item.icon size={20} />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* User section */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                  <span className="text-emerald-600 font-medium">
                    {user?.name?.charAt(0) || 'A'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">
                    {user?.name || 'Admin'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  title="Déconnexion"
                >
                  <LogOut size={18} />
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-h-screen lg:min-h-[calc(100vh)]">
          <div className="p-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
